/**
 * Browser-side signals: behaviour the server can't see (impressions, clicks,
 * page views, dwell). Events are queued and sent in small batches to
 * POST /api/signals/batch/. The backend decides what is kept and whether it is
 * tied to the member; see tappanon_backend/signals/registry.py.
 *
 * Tracking must never affect the page: every path here swallows its errors,
 * and it uses fetch directly rather than the api instance so a failed batch
 * can't trigger the 401 → /login redirect.
 */
import { BASE_URL } from "@/lib/api";

export type SignalProps = Record<string, string | number | boolean | string[] | null | undefined>;

interface QueuedSignal {
  key: string;
  object_id?: string;
  props?: SignalProps;
}

const ENDPOINT = `${BASE_URL ?? ""}/api/signals/batch/`;
const MAX_BATCH = 50;
const MAX_QUEUE = 200;
const FLUSH_MS = 5000;

const enabled = import.meta.env.VITE_SIGNALS_ENABLED !== "false" && import.meta.env.MODE !== "test";

let queue: QueuedSignal[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;

function send(events: QueuedSignal[], keepalive: boolean) {
  if (typeof fetch !== "function") return;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const token = localStorage.getItem("access_token");
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // localStorage unavailable
  }
  try {
    // The timer and page-hide flushes run outside track()'s try, so an
    // unserialisable prop must be caught here or it escapes as an uncaught error.
    fetch(ENDPOINT, {
      method: "POST",
      headers,
      body: JSON.stringify({ surface: "web", events }),
      credentials: "include",
      keepalive,
    }).catch(() => undefined);
  } catch {
    // drop the batch, never break the page
  }
}

export function flushSignals(keepalive = false) {
  if (timer) {
    clearTimeout(timer);
    timer = undefined;
  }
  while (queue.length) {
    send(queue.splice(0, MAX_BATCH), keepalive);
  }
}

export function track(key: string, options: { objectId?: string | number | null; props?: SignalProps } = {}) {
  if (!enabled || typeof window === "undefined") return;
  try {
    const event: QueuedSignal = { key };
    if (options.objectId !== undefined && options.objectId !== null && options.objectId !== "") {
      event.object_id = String(options.objectId);
    }
    if (options.props) event.props = options.props;
    queue.push(event);
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    if (queue.length >= MAX_BATCH) {
      flushSignals();
    } else if (!timer) {
      timer = setTimeout(() => flushSignals(), FLUSH_MS);
    }
  } catch {
    // never break the page
  }
}

if (enabled && typeof window !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushSignals(true);
  });
  window.addEventListener("pagehide", () => flushSignals(true));
}
